/**
 * Sentry Utility Functions
 * Helper functions for manual error capture, context setting, and breadcrumbs
 * 
 * These utilities provide a consistent interface for Sentry operations across the codebase.
 * Prefer using these utilities over direct Sentry.* calls for better maintainability.
 * 
 * All functions include error handling guards to prevent Sentry failures from crashing the app.
 */

import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration } from '@sentry/react';
import { ERROR_LEVELS } from '../../constants/sentry-constants';

/**
 * Get Sentry browser tracing integration
 * 
 * Exported for use in Sentry initialization.
 * This is a direct export of the integration function from @sentry/react.
 * 
 * Usage: browserTracingIntegration({ enableInp: true })
 */
export { browserTracingIntegration };

/**
 * Get Sentry replay integration
 * 
 * Exported for use in Sentry initialization.
 * This is a direct export of the integration function from @sentry/react.
 * 
 * Usage: replayIntegration({ maskAllText: false })
 */
export { replayIntegration };

/**
 * Check if Sentry is initialized
 * @returns true if Sentry client exists, false otherwise
 */
function isSentryInitialized(): boolean {
  try {
    return Sentry.getClient() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Check if Sentry warnings should be shown
 * Only warn if Sentry is expected to be enabled (DSN provided and enabled flag is true)
 * @returns true if warnings should be shown, false otherwise
 */
function shouldWarnAboutSentry(): boolean {
  if (!import.meta.env.DEV) {
    return false; // Never warn in production
  }
  
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false';
  
  // Only warn if Sentry is enabled and DSN is provided but still not initialized
  return sentryEnabled && !!sentryDsn;
}

/**
 * Sentry User interface with known properties
 */
interface SentryUser {
  id?: string;
  email?: string;
  username?: string;
  accountId?: string;
  accountName?: string;
  subDomain?: string;
  [key: string]: unknown; // Allow custom attributes
}

/**
 * Manually capture an exception
 * 
 * @sideEffect - Makes network call to Sentry service
 * 
 * @param error - Error object or error message
 * @param context - Additional context data
 */
export function captureException(
  error: Error | string,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
    contexts?: Record<string, Record<string, unknown>>;
  }
): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot capture exception - Sentry not initialized (DSN provided but initialization failed)');
    }
    return;
  }

  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    
    Sentry.captureException(errorObj, {
      tags: context?.tags,
      extra: context?.extra,
      level: context?.level || ERROR_LEVELS.ERROR,
      contexts: context?.contexts,
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to capture exception:', err);
    }
  }
}

/**
 * Add a breadcrumb
 * 
 * @sideEffect - Modifies Sentry scope state
 * 
 * Validation requirements:
 * - At least one of `message` or `category` must be present
 * - `level` must be a valid Sentry.SeverityLevel if provided
 * 
 * @param breadcrumb - Breadcrumb object with message, category, level, data, etc.
 * @throws {Error} If breadcrumb validation fails
 */
export function addBreadcrumb(breadcrumb: {
  message?: string;
  category?: string;
  type?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
  timestamp?: number;
}): void {
  // Validation: require at least message or category
  if (!breadcrumb.message && !breadcrumb.category) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Breadcrumb validation failed: message or category required');
    }
    return;
  }

  // Validation: level must be valid if provided
  if (breadcrumb.level) {
    const validLevels: Sentry.SeverityLevel[] = ['fatal', 'error', 'warning', 'info', 'debug', 'log'];
    if (!validLevels.includes(breadcrumb.level)) {
      if (import.meta.env.DEV) {
        console.warn('[Sentry] Breadcrumb validation failed: invalid level', breadcrumb.level);
      }
      return;
    }
  }

  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot add breadcrumb - Sentry not initialized (DSN provided but initialization failed)');
    }
    return;
  }

  try {
    Sentry.addBreadcrumb({
      ...breadcrumb,
      timestamp: breadcrumb.timestamp || Date.now() / 1000,
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to add breadcrumb:', err);
    }
  }
}

/**
 * Set custom tag
 * 
 * @sideEffect - Modifies Sentry scope state
 * 
 * @param key - Tag key
 * @param value - Tag value
 */
export function setTag(key: string, value: string): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot set tag - Sentry not initialized (DSN provided but initialization failed)');
    }
    return;
  }

  try {
    Sentry.setTag(key, value);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to set tag:', err);
    }
  }
}

/**
 * Set user context in Sentry
 * 
 * @sideEffect - Modifies Sentry scope state
 * 
 * @param user - User object or null to clear
 */
export function setUser(user: SentryUser | null): void {
  if (!isSentryInitialized()) {
    if (shouldWarnAboutSentry()) {
      console.warn('[Sentry] Cannot set user - Sentry not initialized (DSN provided but initialization failed)');
    }
    return;
  }

  try {
    Sentry.setUser(user);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Failed to set user:', err);
    }
  }
}
